import { ApiError } from "@/api/errors";
import { login, loginSelect } from "@/api/login";
import { currentOwnerKey, setToken } from "@/api/session";
import { setSessionDeferred } from "@/offline";
import { type ReloginSecret, forgetSecret, hasSavedSecret } from "@/unlock";
import { useSyncExternalStore } from "react";

type ReloginOutcome =
  | { status: "renewed"; token: string }
  | { status: "unavailable" }
  | { status: "deferred" }
  | { status: "rejected" }
  | { status: "failed"; error: unknown };

type SecretResolution =
  | { status: "ready"; secret: ReloginSecret }
  | { status: "unavailable" }
  | { status: "deferred" };

type UnlockPrompt = () => Promise<ReloginSecret | null>;

let secretInMemory: ReloginSecret | null = null;
let unlockPrompt: UnlockPrompt | null = null;
let inFlight: Promise<ReloginOutcome> | null = null;
let deferred = false;

const deferralListeners = new Set<() => void>();

function setDeferred(value: boolean): void {
  if (deferred === value) {
    return;
  }
  deferred = value;
  setSessionDeferred(value);
  for (const listener of deferralListeners) {
    listener();
  }
}

export function isReloginDeferred(): boolean {
  return deferred;
}

function subscribeToReloginDeferral(listener: () => void): () => void {
  deferralListeners.add(listener);
  return () => {
    deferralListeners.delete(listener);
  };
}

export function resumeRelogin(): void {
  setDeferred(false);
}

export function deferRelogin(): void {
  setDeferred(true);
}

export function useReloginDeferred(): boolean {
  return useSyncExternalStore(subscribeToReloginDeferral, isReloginDeferred, () => false);
}

export function rememberSecret(secret: ReloginSecret): void {
  secretInMemory = secret;
  setDeferred(false);
}

export function forgetRememberedSecret(): void {
  secretInMemory = null;
  setDeferred(false);
}

export function completeSecretTenant(tenantId: string): void {
  if (secretInMemory && secretInMemory.tenantId === null) {
    secretInMemory = { ...secretInMemory, tenantId };
  }
}

export function rememberedSecret(): ReloginSecret | null {
  return secretInMemory;
}

export function setUnlockPrompt(prompt: UnlockPrompt | null): void {
  unlockPrompt = prompt;
}

export function attemptRelogin(): Promise<ReloginOutcome> {
  if (!inFlight) {
    inFlight = runRelogin().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

async function runRelogin(): Promise<ReloginOutcome> {
  const resolution = await resolveSecret();
  if (resolution.status !== "ready") {
    return resolution;
  }
  const secret = resolution.secret;
  secretInMemory = secret;
  try {
    const token = await authenticate(secret);
    if (!token) {
      return { status: "unavailable" };
    }
    setToken(token);
    return { status: "renewed", token };
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      secretInMemory = null;
      await forgetSecret();
      return { status: "rejected" };
    }
    setDeferred(true);
    return { status: "failed", error };
  }
}

async function resolveSecret(): Promise<SecretResolution> {
  if (deferred) {
    return { status: "deferred" };
  }
  if (secretInMemory) {
    return { status: "ready", secret: secretInMemory };
  }
  if (!unlockPrompt || !(await hasSavedSecret(currentOwnerKey()))) {
    return { status: "unavailable" };
  }
  const unlocked = await unlockPrompt();
  return unlocked ? { status: "ready", secret: unlocked } : { status: "deferred" };
}

async function authenticate(secret: ReloginSecret): Promise<string | null> {
  const first = await login(secret.identifier, secret.password);
  if (first.status === "authenticated") {
    return first.access_token ?? null;
  }
  if (!first.disambiguation_token || !secret.tenantId) {
    return null;
  }
  const chosen = await loginSelect(first.disambiguation_token, secret.tenantId);
  return chosen.status === "authenticated" ? (chosen.access_token ?? null) : null;
}
